/* =========================================================================
   NHASS — global.js
   - Reveal animations, mobile menu, header scroll state
   - AJAX cart drawer: ATC intercept, qty change, remove, Section Rendering API
   ========================================================================= */

(function () {
  'use strict';

  /* --- IntersectionObserver reveal --- */
  const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  if (!prefersReduced && 'IntersectionObserver' in window) {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0.05 });
    document.querySelectorAll('.reveal').forEach(el => io.observe(el));
  } else {
    document.querySelectorAll('.reveal').forEach(el => el.classList.add('is-in'));
  }

  /* --- Header scroll state --- */
  const header = document.querySelector('[data-header]');
  if (header) {
    const onScroll = () => header.classList.toggle('is-scrolled', window.scrollY > 24);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  const focusables = root => [...root.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex="0"]')].filter(el => el.getClientRects().length && !el.closest('[inert]'));
  const trapFocus = (event, root) => {
    if (event.key !== 'Tab') return;
    const items = focusables(root);
    if (!items.length) { event.preventDefault(); return; }
    const first = items[0], last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || !root.contains(document.activeElement))) { event.preventDefault(); last.focus(); }
    else if (!event.shiftKey && (document.activeElement === last || !root.contains(document.activeElement))) { event.preventDefault(); first.focus(); }
  };
  const menuBtn = document.querySelector('[data-menu-toggle]');
  const mobileNav = document.querySelector('[data-mobile-nav]');
  const closeMenu = () => {
    if (!mobileNav || !mobileNav.classList.contains('is-open')) return;
    mobileNav.classList.remove('is-open');
    mobileNav.setAttribute('aria-hidden', 'true');
    mobileNav.inert = true;
    menuBtn.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    menuBtn.focus();
  };
  if (menuBtn && mobileNav) {
    menuBtn.addEventListener('click', () => {
      if (mobileNav.classList.contains('is-open')) { closeMenu(); return; }
      mobileNav.inert = false;
      mobileNav.setAttribute('aria-hidden', 'false');
      mobileNav.classList.add('is-open');
      menuBtn.setAttribute('aria-expanded', 'true');
      document.body.style.overflow = 'hidden';
      focusables(mobileNav)[0]?.focus();
    });
    mobileNav.addEventListener('click', event => { if (event.target.closest('[data-mobile-close], a')) closeMenu(); });
    document.addEventListener('keydown', event => {
      if (!mobileNav.classList.contains('is-open')) return;
      if (event.key === 'Escape') closeMenu();
      else trapFocus(event, mobileNav);
    });
  }

  /* =========================================================================
     CART DRAWER
     ========================================================================= */
  const NHASS = window.NHASS = window.NHASS || {};

  const rootPath = window.Shopify?.routes?.root || '/';
  const messages = window.NHASS.messages || {};
  const Cart = {
    changing: false,
    returnFocus: null,
    sectionId: 'cart-drawer',

    drawer() { return document.querySelector('[data-cart-drawer]'); },

    open() {
      const d = this.drawer();
      if (!d) return;
      closeMenu();
      if (d.getAttribute('aria-hidden') !== 'false') this.returnFocus = document.activeElement;
      d.inert = false;
      d.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      focusables(d)[0]?.focus();
    },

    close() {
      const d = this.drawer();
      if (!d) return;
      d.inert = true;
      d.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      if (this.returnFocus?.isConnected) this.returnFocus.focus();
    },

    setLoading(on) {
      const d = this.drawer();
      if (d) { d.classList.toggle('is-loading', !!on); d.setAttribute('aria-busy', String(!!on)); }
    },

    refresh(sectionHtml) {
      if (!sectionHtml) return;
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHtml, 'text/html');
      const newSection = doc.querySelector('#shopify-section-' + this.sectionId);
      const oldSection = document.querySelector('#shopify-section-' + this.sectionId);
      if (newSection && oldSection) {
        const oldDrawer = oldSection.querySelector('[data-cart-drawer]');
        const wasOpen = oldDrawer && oldDrawer.getAttribute('aria-hidden') === 'false';
        const focused = oldSection.contains(document.activeElement);
        const oldKey = document.activeElement.closest('[data-line-key]')?.dataset.lineKey;
        const action = ['data-line-increase', 'data-line-decrease', 'data-line-remove'].find(name => document.activeElement.hasAttribute(name));
        oldSection.replaceWith(newSection);
        if (wasOpen) {
          const newDrawer = newSection.querySelector('[data-cart-drawer]');
          if (newDrawer) {
            newDrawer.inert = false;
            newDrawer.setAttribute('aria-hidden', 'false');
            if (focused) {
              const row = [...newDrawer.querySelectorAll('[data-line-key]')].find(el => el.dataset.lineKey === oldKey);
              (action && row?.querySelector('[' + action + ']') || focusables(newDrawer)[0])?.focus();
            }
            document.body.style.overflow = 'hidden';
          }
        }
      }
    },

    updateCount(count) {
      document.querySelectorAll('[data-cart-count]').forEach(el => { el.textContent = count; });
    },

    async add(formData) {
      this.setLoading(true);
      formData.append('sections', this.sectionId);
      try {
        const res = await fetch(rootPath + 'cart/add.js', {
          method: 'POST',
          headers: { 'Accept': 'application/javascript' },
          body: formData
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || messages.addError || 'Toevoegen is niet gelukt. Probeer het opnieuw.');
        await this.fetchAndRefresh();
        this.open();
        return data;
      } catch (err) {
        console.error('[NHASS cart] add failed:', err);
        alert(err.message || messages.addError || 'Toevoegen is niet gelukt. Probeer het opnieuw.');
        throw err;
      } finally {
        this.setLoading(false);
      }
    },

    async change(lineKey, quantity) {
      if (this.changing) return;
      this.changing = true;
      this.setLoading(true);
      try {
        const res = await fetch(rootPath + 'cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/javascript' },
          body: JSON.stringify({ id: lineKey, quantity: quantity, sections: this.sectionId })
        });
        const data = await res.json();
        if (!res.ok) throw new Error(data.description || data.message || messages.changeError);
        this.updateCount(data.item_count);
        if (data.sections && data.sections[this.sectionId]) {
          this.refresh(data.sections[this.sectionId]);
        } else {
          await this.fetchAndRefresh();
        }
      } catch (err) {
        console.error('[NHASS cart] change failed:', err);
        alert(err.message || messages.changeError || 'De hoeveelheid kon niet worden aangepast.');
      } finally {
        this.changing = false;
        this.setLoading(false);
      }
    },

    async fetchAndRefresh() {
      const [cartRes, sectionRes] = await Promise.all([
        fetch(rootPath + 'cart.js', { headers: { 'Accept': 'application/json' } }),
        fetch(rootPath + '?sections=' + this.sectionId, { headers: { 'Accept': 'text/html' } })
      ]);
      if (!cartRes.ok || !sectionRes.ok) throw new Error(messages.loadError || 'Je winkelmandje kon niet worden vernieuwd.');
      const cart = await cartRes.json();
      const sections = await sectionRes.json();
      this.updateCount(cart.item_count);
      if (sections[this.sectionId]) this.refresh(sections[this.sectionId]);
    }
  };

  NHASS.cart = Cart;

  document.addEventListener('click', (e) => {
    const cartLink = e.target.closest('[data-cart-link]');
    if (cartLink) {
      e.preventDefault();
      Cart.open();
      return;
    }

    if (e.target.closest('[data-cart-close]')) {
      const link = e.target.closest('a[data-cart-close]');
      if (!link) e.preventDefault();
      Cart.close();
      return;
    }

    if (e.target.closest('[data-cart-checkout]')) {
      e.preventDefault();
      window.location.href = '/checkout?locale=nl';
      return;
    }

    const decBtn = e.target.closest('[data-line-decrease]');
    const incBtn = e.target.closest('[data-line-increase]');
    if (decBtn || incBtn) {
      e.preventDefault();
      const item = (decBtn || incBtn).closest('[data-line-key]');
      if (!item) return;
      const key = item.dataset.lineKey;
      const qtyEl = item.querySelector('[data-line-qty]');
      const cur = parseInt((qtyEl && (qtyEl.textContent || qtyEl.value)) || '1', 10);
      const next = decBtn ? Math.max(0, cur - 1) : cur + 1;

      const cartPageForm = item.closest('form[action*="/cart"]');
      if (cartPageForm && !item.closest('[data-cart-drawer]')) {
        if (qtyEl) qtyEl.value = next;
        cartPageForm.submit();
        return;
      }

      Cart.change(key, next);
      return;
    }

    const remBtn = e.target.closest('[data-line-remove]');
    if (remBtn) {
      e.preventDefault();
      const item = remBtn.closest('[data-line-key]');
      if (!item) return;

      const cartPageForm = item.closest('form[action*="/cart"]');
      if (cartPageForm && !item.closest('[data-cart-drawer]')) {
        const qtyEl = item.querySelector('[data-line-qty]');
        if (qtyEl) qtyEl.value = 0;
        cartPageForm.submit();
        return;
      }

      Cart.change(item.dataset.lineKey, 0);
      return;
    }
  });

  document.addEventListener('submit', (e) => {
    if (e.submitter?.name === 'checkout') {
      e.preventDefault();
      const checkoutForm = e.target;
      if (checkoutForm.dataset.submitting === 'true') return;
      checkoutForm.dataset.submitting = 'true';
      const button = e.submitter;
      button.disabled = true;
      fetch(rootPath + 'cart/update.js', {method:'POST', body:new FormData(checkoutForm), headers:{Accept:'application/json'}})
        .then(async response => { const data = await response.json(); if (!response.ok) throw new Error(data.description || data.message || messages.changeError); window.location.href = '/checkout?locale=nl'; })
        .catch(error => { alert(error.message || messages.changeError); })
        .finally(() => { delete checkoutForm.dataset.submitting; button.disabled = false; });
      return;
    }
    const form = e.target.closest('form[action*="/cart/add"]');
    if (!form) return;
    e.preventDefault();
    if (form.dataset.submitting === 'true') return;
    const fd = new FormData(form);
    const buttons = Array.from(form.querySelectorAll('button[type="submit"], input[type="submit"]'));
    const disabledStates = buttons.map(button => button.disabled);
    form.dataset.submitting = 'true';
    form.setAttribute('aria-busy', 'true');
    buttons.forEach(button => { button.disabled = true; });
    Cart.add(fd).catch(() => {}).finally(() => {
      delete form.dataset.submitting;
      form.removeAttribute('aria-busy');
      const selected = form.querySelector('[data-variant-select]')?.selectedOptions[0];
      buttons.forEach((button, index) => { button.disabled = selected ? selected.dataset.available !== 'true' : disabledStates[index]; });
    });
  });

  document.addEventListener('keydown', (e) => {
    const activeDrawer = Cart.drawer();
    if (activeDrawer?.getAttribute('aria-hidden') === 'false') trapFocus(e, activeDrawer);
    if (e.key === 'Escape') {
      const d = Cart.drawer();
      if (d && d.getAttribute('aria-hidden') === 'false') Cart.close();
    }
  });
})();

